<template>
  <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2">
    <label
      :for="fieldPath"
      class="block text-sm font-medium text-gray-900 sm:pt-1.5"
    >
      {{ fieldLabel }}
      <div class="text-xs text-gray-400 mt-1">
        {{ fieldDescription }}
      </div>
    </label>

    <div class="mt-2 sm:col-span-2 sm:mt-0">
      <div class="space-y-4 w-full max-w-3xl">
        <ul role="list" class="space-y-4 mb-5">
          <div v-for="(document, index) in modelValue" :key="index">
            <li v-if="document.attributes.categoryName === props.categoryName" class="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
              <h3 class="flex items-center justify-between mb-3">
                <span class="text-sm text-gray-700">Document #{{ index + 1 }}</span>
                <button @click.prevent="remove(index)" class="text-red-600 hover:text-red-800 transition-transform duration-200 hover:scale-110 flex items-center">
                  <MinusCircleIcon class="size-6 align-middle" aria-hidden="true" />
                </button>
              </h3>
              <div class="flex items-center justify-between">
                <span v-if="document.attributes.file" class="ml-3 text-sm text-gray-600 truncate max-w-[300px]">
                  <a
                    :href="document.attributes.file.url || '#'"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="ml-3 text-sm text-gray-600 truncate max-w-[300px]"
                  >
                    {{ document.attributes.file.name }}
                  </a>
                </span>
                <span v-else class="ml-3 text-sm italic text-gray-400">
                  {{ $gettext("Pas de fichier sélectionné") }}
                </span>
              </div>
            </li>
          </div>
        </ul>
      </div>

      <p v-if="fileValidationError" class="mt-2 text-sm text-red-600">
        {{ fileValidationError }}
      </p>

      <input
        ref="fileInput"
        type="file"
        class="sr-only"
        :accept="allowedDocumentTypeStore.acceptString"
        v-bind="fieldAttrs"
        @change="handleNewFile"
      />

      <button
        @click.prevent="triggerFileInput"
        class="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-900 transition-transform duration-200 hover:scale-110"
      >
        <PlusCircleIcon class="size-6" aria-hidden="true" />
        <span class="text-sm">{{ $gettext("Ajouter un fichier") }}</span>
      </button>

      <p v-if="errors[fieldPath]" class="mt-2 text-sm text-red-600">
        {{ errors[fieldPath] }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { PlusCircleIcon, MinusCircleIcon } from "@heroicons/vue/24/outline";
import { ref } from "vue";
import { useGettext } from "vue3-gettext";
import { useAllowedDocumentTypeStore } from "@/stores/allowedDocumentType";

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  fieldPath: String,
  fieldAttrs: Object,
  fieldLabel: String,
  fieldDescription: String,
  errors: Object,
  categoryName: String
});

const emit = defineEmits(["update:modelValue"]);
const { $gettext } = useGettext();
const allowedDocumentTypeStore = useAllowedDocumentTypeStore();
const fileInput = ref(null);
const fileValidationError = ref(null);

const triggerFileInput = () => {
  fileValidationError.value = null;
  fileInput.value?.click();
};

const handleNewFile = (event) => {
  const file = event.target.files[0];
  if (!file) return;

  fileValidationError.value = null;

  const ext = file.name.split(".").pop().toLowerCase();
  const maxSize = allowedDocumentTypeStore.maxSizeFor(ext);
  if (!allowedDocumentTypeStore.allowedExtensions.includes(ext)) {
    fileValidationError.value = $gettext("Le fichier n'est pas autorisé. Seuls les fichiers de type %{extensions} sont autorisés.", { extensions: allowedDocumentTypeStore.allowedExtensions.join(", ") });
    return;
  }
  if (maxSize && file.size > maxSize * 1024 * 1024) {
    fileValidationError.value = $gettext("Le fichier est trop grand. La taille maximale autorisée est de %{maxSize} Mo.", { maxSize: maxSize });
    return;
  }

  const newDocument = {
    attributes: {
      file,
      categoryName: props.categoryName
    }
  };

  emit("update:modelValue", [...props.modelValue, newDocument]);
  event.target.value = "";
};

const remove = (index) => {
  const updated = [...props.modelValue];
  updated.splice(index, 1);
  emit("update:modelValue", updated);
};
</script>
